import type { CaptionSourceHistoryEvidence } from '@/lib/translation/captionHydrationFrozenPrefix';
/**
 * Root transaction for immutable source words and the text-free translation
 * ledger. No source words enter TranslationState. All existing dispatchers use
 * this boundary through the real root reducer; no second cache dispatch exists.
 */
import { createAction, type UnknownAction } from '@reduxjs/toolkit';
import { validateSourceTurnV1, type SourceTurnV1 } from '@/lib/translation/contracts';
import translationReducer, {
  clearTranslations, translationCallScopeSet, translationSourceTurnObserved,
  translationCaptionHydrationApplied, translationProjectionBatchApplied,
  translationTranscriptCorrelated, translationRevisionAppended,
  translationRevisionSelected, translationTurnMarkedUnavailable,
  preflightTranslationCaptionHydration, preflightTranslationProjectionBatch,
  type TranslationState, type TranslationSourceTurnRef,
} from './slices/translationSlice';

export interface SourceTurnTextState {
  readonly callId: string | null;
  readonly byId: Readonly<Record<string, Readonly<SourceTurnV1>>>;
  /** Capability-bound complete traversal through the named server snapshot. */
  readonly history?: Readonly<CaptionSourceHistoryEvidence>;
}
export interface TranslationHistoryState {
  translation: TranslationState;
  sourceTurnText: SourceTurnTextState;
}
export type TranslationHistoryAdmission = {
  accepted: boolean;
  state: TranslationHistoryState;
  reason: 'source_invalid' | 'source_conflict' | 'translation_refused' | null;
};
export interface ArchiveProjectionPayload {
  call_id: string;
  actions: Array<{ type: string; payload: unknown }>;
}
export const translationArchiveProjectionApplied = createAction<ArchiveProjectionPayload>(
  'translationHistory/archiveProjectionApplied',
);
const EMPTY_SOURCES: SourceTurnTextState['byId'] = Object.freeze({});
const ARCHIVE_ACTIONS: ReadonlySet<string> = new Set([
  translationSourceTurnObserved.type, translationTranscriptCorrelated.type,
  translationRevisionAppended.type, translationRevisionSelected.type,
  translationTurnMarkedUnavailable.type,
]);
function emptySourceText(callId: string | null): SourceTurnTextState {
  return Object.freeze({ callId, byId: EMPTY_SOURCES });
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
/** Immutable source fields must agree. The ONE transitional exception is
 * detected_language: its existing producer contract explicitly equates absent
 * and null (source_turn_ledger.py:993; source_turn_persistence.py:380). Compare
 * that statement without rewriting either genuine received source record. */
function sameFullSource(a: Readonly<SourceTurnV1>, b: SourceTurnV1): boolean {
  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  const keys = Object.keys(left).filter(key => key !== 'detected_language');
  const rightKeys = Object.keys(right).filter(key => key !== 'detected_language');
  return keys.length === rightKeys.length && keys.every(key =>
    Object.prototype.hasOwnProperty.call(right, key) && left[key] === right[key]) &&
    (a.detected_language ?? null) === (b.detected_language ?? null);
}
function matchesAdmittedRef(ref: TranslationSourceTurnRef | undefined, source: SourceTurnV1): boolean {
  return ref !== undefined && ref.source_turn_id === source.source_turn_id &&
    ref.call_id === source.call_id && ref.stable_participant_id === source.stable_participant_id &&
    ref.deepgram_segment_id === source.deepgram_segment_id && ref.source_epoch === source.source_epoch &&
    (ref.source_track_sid === undefined || ref.source_track_sid === source.source_track_sid) &&
    (ref.speaker_role === undefined || ref.speaker_role === source.speaker_role) &&
    ref.ordinal === source.ordinal && ref.started_at_ms === source.started_at_ms &&
    ref.source_language === source.source_language && ref.detected_language === (source.detected_language ?? null);
}

/** Pure preview AND actual root admission. Owners must use this same function
 * to decide whether a dispatch succeeded; translation-only preview is weaker. */
export function preflightTranslationHistory(
  previous: { translation: TranslationState; sourceTurnText?: SourceTurnTextState } | undefined, action: UnknownAction,
): TranslationHistoryAdmission {
  const priorTranslation = previous?.translation ?? translationReducer(undefined, { type: '@@translationHistory/init' });
  // Legacy preloaded roots may have lineage but no text cache. Adopt only an
  // empty cache; never synthesize full sources from those old text-free refs.
  const before: TranslationHistoryState = {
    translation: priorTranslation,
    sourceTurnText: previous?.sourceTurnText ?? emptySourceText(priorTranslation.callId),
  };
  const refuse = (reason: Exclude<TranslationHistoryAdmission['reason'], null>): TranslationHistoryAdmission =>
    ({ accepted: false, state: before, reason });
  let translation: TranslationState;
  let sources: SourceTurnV1[] = [];
  if (translationCaptionHydrationApplied.match(action)) {
    const preview = preflightTranslationCaptionHydration(before.translation, action.payload);
    if (!preview.accepted) return refuse('translation_refused');
    translation = preview.state;
    sources = action.payload.source_turns.map(item => item.source_turn);
  } else if (translationProjectionBatchApplied.match(action)) {
    const preview = preflightTranslationProjectionBatch(before.translation, action.payload);
    if (!preview.accepted) return refuse('translation_refused');
    translation = preview.state;
    sources = action.payload.actions.filter(entry => entry.type === translationSourceTurnObserved.type)
      .map(entry => entry.payload as SourceTurnV1);
  } else if (translationArchiveProjectionApplied.match(action)) {
    const payload: unknown = action.payload;
    if (!isRecord(payload) || Object.keys(payload).length !== 2 ||
        typeof payload.call_id !== 'string' || payload.call_id.length === 0 || !Array.isArray(payload.actions)) {
      return refuse('translation_refused');
    }
    // This archive transaction deliberately adopts the archive's call scope.
    // Its sole dispatcher (useHistoricalTranslationProjection) must match the
    // response call ID to its requested UUID and cancel inactive/aborted effects.
    // Nested scope actions remain excluded by ARCHIVE_ACTIONS above.
    let staged = translationReducer(before.translation, translationCallScopeSet(payload.call_id));
    const rejectedBefore = staged.rejected.count;
    for (const entry of payload.actions) {
      if (!isRecord(entry) || typeof entry.type !== 'string' || !ARCHIVE_ACTIONS.has(entry.type) ||
          !isRecord(entry.payload)) return refuse('translation_refused');
      staged = translationReducer(staged, entry as unknown as UnknownAction);
      if (entry.type === translationSourceTurnObserved.type) sources.push(entry.payload as unknown as SourceTurnV1);
    }
    // Preserve the archive's existing admission rules, including legacy
    // selections without a generation. Such selections cannot attest assembly.
    if (staged.rejected.count !== rejectedBefore) return refuse('translation_refused');
    translation = staged;
  } else {
    translation = translationReducer(before.translation, action);
    if (translationSourceTurnObserved.match(action)) sources = [action.payload];
  }

  const resets = translation.callId !== before.translation.callId || clearTranslations.match(action);
  let sourceTurnText = resets ? emptySourceText(translation.callId) : before.sourceTurnText;
  let additions: Record<string, Readonly<SourceTurnV1>> | null = null;
  for (const source of sources) {
    if (!validateSourceTurnV1(source).valid || source.call_id !== translation.callId ||
        !matchesAdmittedRef(translation.turns[source.source_turn_id], source)) {
      // Preserve the actual standalone reducer's diagnostic counter, while
      // never publishing source text or an accepted translation fact for it.
      if (translationSourceTurnObserved.match(action) && translation.rejected.count > before.translation.rejected.count) {
        return { accepted: false, state: { translation, sourceTurnText: before.sourceTurnText }, reason: 'source_invalid' };
      }
      return refuse('source_invalid');
    }
    const existing = additions?.[source.source_turn_id] ?? sourceTurnText.byId[source.source_turn_id];
    if (existing !== undefined) {
      if (!sameFullSource(existing, source)) return refuse('source_conflict');
      continue;
    }
    additions ??= { ...sourceTurnText.byId };
    // Copy before freezing: the transport producer retains its own object.
    additions[source.source_turn_id] = Object.freeze({ ...source });
  }
  if (additions !== null) sourceTurnText = Object.freeze({
    ...sourceTurnText, callId: translation.callId, byId: Object.freeze(additions),
  });
  if (translationCaptionHydrationApplied.match(action) && action.payload.source_history) {
    // The same root preview admitted the full source rows and exact index.
    // Preserve server terminal facts only after that whole transaction succeeds.
    const evidence = action.payload.source_history;
    sourceTurnText = Object.freeze({ ...sourceTurnText, history: Object.freeze({ ...evidence,
      source_turn_ids: Object.freeze([...evidence.source_turn_ids]) }) });
  }
  return { accepted: true, state: { translation, sourceTurnText }, reason: null };
}
